
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Tag, Video, FileText, Filter } from 'lucide-react';
import { getAllBlogPosts, getAllCategories, getBlogPostsByCategory, getBlogPostsByType } from '@/lib/content';
import type { BlogPost } from '@/types/blog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const BlogList = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [contentType, setContentType] = useState<'all' | 'traditional' | 'video'>('all');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Load categories on mount
  useEffect(() => {
    getAllCategories().then(cats => {
      setCategories(['all', ...cats]);
    });
  }, []);

  // Load posts when category or content type changes
  useEffect(() => {
    setLoading(true);

    const loadPosts = async () => {
      // Primeiro filtra por tipo de conteúdo
      let allPosts = await getBlogPostsByType(contentType);

      // Depois filtra por categoria se não for 'all'
      if (activeCategory !== 'all') {
        allPosts = allPosts.filter(post =>
          post.categories.some(cat => cat.toLowerCase() === activeCategory.toLowerCase())
        );
      }

      return allPosts;
    };

    loadPosts().then(setPosts).finally(() => setLoading(false));
  }, [activeCategory, contentType]);

  const filteredPosts = posts;
    
  if (loading) {
    return (
      <section className="section container">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Carregando posts...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="section container">
      {/* Filtros de tipo de conteúdo */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Tipo de conteúdo:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={contentType === 'all' ? "default" : "outline"}
            size="sm"
            onClick={() => setContentType('all')}
            className={contentType === 'all' ? "bg-portfolio-primary hover:bg-portfolio-primary/90" : ""}
          >
            <FileText className="h-4 w-4 mr-2" />
            Todos os posts
          </Button>
          <Button
            variant={contentType === 'traditional' ? "default" : "outline"}
            size="sm"
            onClick={() => setContentType('traditional')}
            className={contentType === 'traditional' ? "bg-portfolio-primary hover:bg-portfolio-primary/90" : ""}
          >
            <FileText className="h-4 w-4 mr-2" />
            Artigos tradicionais
          </Button>
          <Button
            variant={contentType === 'video' ? "default" : "outline"}
            size="sm"
            onClick={() => setContentType('video')}
            className={contentType === 'video' ? "bg-portfolio-primary hover:bg-portfolio-primary/90" : ""}
          >
            <Video className="h-4 w-4 mr-2" />
            Artigos com vídeo
          </Button>
        </div>
      </div>

      {/* Filtros de categoria */}
      <div className="mb-10">
        <div className="flex items-center gap-2 mb-3">
          <Tag className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">Categorias:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <Button
              key={category}
              variant={activeCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveCategory(category)}
              className={activeCategory === category ? "bg-portfolio-primary hover:bg-portfolio-primary/90" : ""}
            >
              {category === 'all' ? 'Todas' : category}
            </Button>
          ))}
        </div>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredPosts.map(post => {
          // Se o post tem vídeo, usar thumbnail do Panda Videos
          const imageUrl = post.video?.enabled && post.video.embedUrl
            ? (() => {
                // Extrair videoId da embedUrl
                const match = post.video.embedUrl.match(/[?&]v=([^&]+)/);
                const videoId = match ? match[1] : null;
                // Extrair libraryId curto da embedUrl (formato: vz-XXXXX)
                const libraryMatch = post.video.embedUrl.match(/player-(vz-[^.]+)/);
                const libraryId = libraryMatch ? libraryMatch[1] : null;

                if (videoId && libraryId) {
                  return `https://player-${libraryId}.tv.pandavideo.com.br/embed/thumbnail/${videoId}.jpg`;
                }
                return post.coverImage;
              })()
            : post.coverImage;

          return (
            <Card key={post.slug} className="overflow-hidden border card-hover gradient-border">
              <Link to={`/blog/${post.slug}`} className="block">
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src={imageUrl}
                    alt={post.title}
                    loading="lazy"
                    width={640}
                    height={360}
                    className="w-full h-full object-cover transition-transform hover:scale-105 duration-500"
                  />
                  {/* Indicador de vídeo no thumbnail */}
                  {post.video?.enabled && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <div className="bg-primary/90 rounded-full p-4">
                        <Video className="h-8 w-8 text-primary-foreground" />
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            
            <div className="p-6">
              <div className="flex flex-wrap gap-2 mb-4">
                {/* Badge de vídeo se o post tiver vídeo */}
                {post.video?.enabled && (
                  <Badge
                    variant="secondary"
                    className="bg-primary/10 text-primary hover:bg-primary/20 font-medium"
                  >
                    <Video size={12} className="mr-1" />
                    Com vídeo
                  </Badge>
                )}

                {/* Categorias */}
                {post.categories.map((category, i) => (
                  <Badge
                    key={i}
                    variant="secondary"
                    className="bg-portfolio-primary/10 text-portfolio-primary hover:bg-portfolio-primary/20"
                  >
                    <Tag size={12} className="mr-1" /> {category}
                  </Badge>
                ))}
              </div>
              
              <Link to={`/blog/${post.slug}`} className="block">
                <h3 className="text-xl md:text-2xl font-bold mb-2 hover:text-portfolio-primary transition-colors">
                  {post.title}
                </h3>
              </Link>
              
              <p className="text-muted-foreground mb-4 line-clamp-3">{post.excerpt}</p>
              
              <div className="flex items-center text-sm text-muted-foreground mb-4">
                <span className="flex items-center mr-4">
                  <Calendar size={14} className="mr-1" /> {format(post.date, 'dd MMM yyyy', { locale: ptBR })}
                </span>
                <span className="flex items-center">
                  <Clock size={14} className="mr-1" /> {post.readingTime} min de leitura
                </span>
              </div>
              
              <Button variant="ghost" size="sm" className="text-portfolio-primary" asChild>
                <Link to={`/blog/${post.slug}`} className="flex items-center group">
                  Ler mais <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
            </Card>
          );
        })}
      </div>
      
      {filteredPosts.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">Nenhum artigo encontrado nesta categoria.</p>
        </div>
      )}
    </section>
  );
};

export default BlogList;
